import React from 'react';
import { ArrowRight, Maximize, Layers, Box, Settings, PenTool, Layout } from 'lucide-react';
import { Link } from 'react-router-dom';
import img1 from "../../assets/space1.jpg";
import img2 from "../../assets/space2.jpg";
import img3 from "../../assets/space3.jpg";
import img4 from "../../assets/space.jpg";
import img5 from "../../assets/space4.jpg";

const designs = [
  { 
    id: 1, 
    title: 'Murphy Wall Beds', 
    category: 'Bedroom', 
    image:img1,
    desc: 'Full-size bed that folds into the wall with a sofa attached.'
  },
  { 
    id: 2, 
    title: 'Foldable Dining Units', 
    category: 'Dining', 
    image: img2,
    desc: '4-seater table that vanishes into a console when not in use.' 
  },
  { 
    id: 3, 
    title: 'Hidden Storage Sofa', 
    category: 'Living', 
    image: img3,
    desc: 'L-shaped sofas with hydraulic lift storage underneath.' 
  },
  { 
    id: 4, 
    title: 'Modular Study Units', 
    category: 'Kids Room', 
    image: img4,
    desc: 'Desks that slide under beds to create play area.'
  },
  { 
    id: 5, 
    title: 'Staircase Drawers', 
    category: 'Utility', 
    image: "https://i.pinimg.com/736x/84/b8/72/84b87236bc65e7b6df672f01b812e61f.jpg",
    desc: 'Each step doubles as a pull-out drawer for shoes/books.'
  },
  { 
    id: 6, 
    title: 'Vertical Balcony Gardens', 
    category: 'Outdoors', 
    image: "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?auto=format&fit=crop&q=80",
    desc: 'Wall-mounted planters to save floor space in balconies.'
  },
];

const SpaceSaving = () => {
  return (
    <div className="font-sans text-gray-800 bg-white">
      
      {/* 1. HERO SECTION */}
      <div
        className="relative h-[70vh] w-full bg-cover bg-center"
        style={{ backgroundImage: `url(${img5})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 animate-fadeIn">
          <h4 className="text-amber-500 font-bold tracking-[0.3em] uppercase mb-3">
             Smart Living
          </h4>
          <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-4">
             Space Saving Furniture
          </h1>
          <p className="text-gray-200 text-lg md:text-xl max-w-2xl font-light leading-relaxed">
             Maximize your home's potential in <strong>Noida & Delhi Apartments</strong>. 
             Smart designs for modern lifestyles.
          </p>
        </div>
      </div>

      {/* 2. INTRO SECTION */}
      <section className="py-20 px-6 bg-white text-center">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
           More Room, Less Clutter
        </h2>
        <div className="w-24 h-1 bg-amber-500 mx-auto mb-8"></div>
        <p className="text-gray-600 max-w-4xl mx-auto leading-loose text-lg">
          Living in a compact apartment in Noida or Delhi doesn't mean compromising on luxury. 
          The biggest challenge in modern flats is storage. Our space-saving collection transforms your home dynamically—turn a 
          living room into a guest bedroom or a wall into a work desk instantly. We use heavy-duty mechanisms that are safe and easy to use.
        </p>
      </section>

      {/* 3. EXPANDED GALLERY */}
      <section className="py-20 bg-neutral-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
             <h3 className="text-4xl font-serif font-bold text-gray-900 mb-2">Smart Solutions Gallery</h3>
             <p className="text-gray-500">Innovative designs for compact homes</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {designs.map((design) => (
              <div key={design.id} className="group relative overflow-hidden shadow-xl cursor-pointer rounded-sm bg-white">
                <div className="h-72 overflow-hidden">
                  <img 
                    src={design.image} 
                    alt={`${design.title} - Space Saving Idea`} 
                    loading="lazy"
                    className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700"
                  />
                </div>
                <div className="p-6">
                  <p className="text-amber-500 text-xs tracking-widest uppercase mb-1 font-bold">{design.category}</p>
                  <h4 className="text-xl font-serif text-gray-900 mb-2">{design.title}</h4>
                  <p className="text-sm text-gray-500 leading-relaxed">{design.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. MECHANISM & TECH (NEW SECTION) */}
      <section className="py-24 px-6 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto">
           <div className="text-center mb-16">
              <h3 className="text-3xl font-serif font-bold">Engineered for Perfection</h3>
              <p className="text-gray-400 mt-2">We use only high-quality hardware brands.</p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
              <div className="flex flex-col items-center p-6 border border-white/10 rounded-xl hover:bg-white/5 transition">
                <Settings className="w-12 h-12 text-amber-500 mb-4" />
                <h4 className="text-xl font-bold mb-3">Hydraulic Lifts</h4>
                <p className="text-gray-400">
                   German-made gas pistons ensure that beds and tables lift effortlessly with just one finger.
                </p>
              </div>
              <div className="flex flex-col items-center p-6 border border-white/10 rounded-xl hover:bg-white/5 transition">
                <Maximize className="w-12 h-12 text-amber-500 mb-4" />
                <h4 className="text-xl font-bold mb-3">Vertical Utilization</h4>
                <p className="text-gray-400">
                   We use floor-to-ceiling lofts and tall units to use 100% of your wall height for storage.
                </p>
              </div>
              <div className="flex flex-col items-center p-6 border border-white/10 rounded-xl hover:bg-white/5 transition">
                <Box className="w-12 h-12 text-amber-500 mb-4" />
                <h4 className="text-xl font-bold mb-3">Invisible Joints</h4>
                <p className="text-gray-400">
                   Furniture looks like a seamless wall panel when closed. No visible handles or hinges.
                </p>
              </div>
           </div>
        </div>
      </section>

      {/* 5. ROOM GUIDE (NEW SECTION) */}
      <section className="py-20 px-6 bg-white">
         <div className="max-w-6xl mx-auto">
            <h3 className="text-3xl font-serif font-bold text-center mb-12">Room-by-Room Guide</h3>
            <div className="space-y-8">
               <div className="flex flex-col md:flex-row gap-8 items-center bg-gray-50 p-8 rounded-lg shadow-sm">
                  <div className="bg-amber-100 p-4 rounded-full text-amber-600"><Layout size={32}/></div>
                  <div>
                     <h4 className="text-xl font-bold mb-2">Living Room</h4>
                     <p className="text-gray-600">Use <strong>Coffee-to-Dining tables</strong> and sofa-cum-beds to host guests without a dedicated guest room.</p>
                  </div>
               </div>
               <div className="flex flex-col md:flex-row gap-8 items-center bg-gray-50 p-8 rounded-lg shadow-sm">
                  <div className="bg-amber-100 p-4 rounded-full text-amber-600"><PenTool size={32}/></div>
                  <div>
                     <h4 className="text-xl font-bold mb-2">Kids Room</h4>
                     <p className="text-gray-600"><strong>Bunk beds with stairs</strong> having drawers are perfect for two kids in one small room.</p>
                  </div>
               </div>
            </div>
         </div>
      </section>

      {/* 6. CTA */}
      <section className="py-24 px-6 text-center bg-amber-50 border-t border-amber-100">
        <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
           Transform Your Compact Space
        </h2>
        <p className="text-gray-600 mb-10 text-lg">
           Visit our showroom to see live demos of these magical furniture pieces.
        </p>
        <Link to="/contact" className="inline-block bg-neutral-900 hover:bg-amber-600 text-white text-lg font-bold px-10 py-4 rounded-sm transition shadow-2xl uppercase tracking-widest">
           View Full Catalog
        </Link>
      </section>
    </div>
  );
};

export default SpaceSaving;